import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where,
} from "firebase/firestore";
import { db as firestore } from "../../config/firebase";
import { Link } from "react-router-dom";
import { PenBox } from "lucide-react";
import BlogEditorButton from "../BlogEditorButton";

function Prepare() {
  const BLOG_CATEGORIES = [
    { id: "dsa", name: "Data Structures & Algorithms" },
    { id: "java", name: "Java Programming" },
    { id: "python", name: "Python" },
    { id: "spring", name: "Spring Framework" },
    { id: "react", name: "React.js" },
    { id: "node", name: "Node.js" },
    { id: "database", name: "Database Systems" },
    { id: "system-design", name: "System Design" },
    { id: "cloud", name: "Cloud Computing" },
    { id: "devops", name: "DevOps" },
    { id: "security", name: "Cybersecurity" },
    { id: "ml", name: "Machine Learning" },
    { id: "web", name: "Web Development" },
    { id: "mobile", name: "Mobile Development" },
    { id: "business", name: "Business & Management" },
    { id: "soft-skills", name: "Soft Skills" },
    { id: "interview", name: "Interview Preparation" },
    { id: "quantitative", name: "Quantitative Aptitude" },
    { id: "articulation", name: "Communication Skills" },
    { id: "career", name: "Career Guidance" },
  ];

  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("dsa");
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isFetchingRef = useRef(false);

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  useEffect(() => {
    if (!searchTerm) return;
    const timer = setTimeout(() => {
      fetchPosts(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchPosts = async (isSearch = false) => {
    if (isFetchingRef.current) return;
    try {
      setIsLoading(true);
      setError(null);
      isFetchingRef.current = true;

      const postsRef = collection(firestore, "posts");
      let q;

      if (isSearch && searchTerm) {
        q = query(
          postsRef,
          where("category", "==", selectedCategory),
          where("content", ">=", searchTerm.toLowerCase()),
          where("content", "<=", searchTerm.toLowerCase() + "\uf8ff"),
          orderBy("content"),
          limit(10)
        );
      } else {
        q = query(
          postsRef,
          where("category", "==", selectedCategory),
          orderBy("createdAt", "desc"),
          limit(10)
        );
      }

      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        setPosts([]);
        setHasMore(false);
        return;
      }

      const newPosts = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          content: data.content || "",
          author: data.author || "Anonymous",
          createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
        };
      });

      setPosts(newPosts);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === 10);
    } catch (error) {
      setError("Failed to load posts. " + error.message);
      setPosts([]);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  const fetchMorePosts = async () => {
    if (!lastVisible || isFetchingRef.current || !hasMore) return;

    try {
      setIsLoading(true);
      isFetchingRef.current = true;

      const postsRef = collection(firestore, "posts");
      const q = query(
        postsRef,
        where("category", "==", selectedCategory),
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        limit(10)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        setHasMore(false);
        return;
      }

      const newPosts = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          content: data.content || "",
          author: data.author || "Anonymous",
          createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
        };
      });

      setPosts((prev) => [...prev, ...newPosts]);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === 10);
    } catch (error) {
      setError("Failed to load more posts.");
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Sidebar (minimizable on mobile) */}
      <div className="lg:w-1/5 w-full">
        {/* Mobile toggle button */}
        <div className="lg:hidden flex justify-between items-center m-4  p-4 ">
          <h2 className="text-lg font-semibold">Categories</h2>
          <button
            onClick={() => setSidebarOpen((open) => !open)}
            className="p-2 rounded bg-blue-600 text-white"
            aria-label="Toggle categories"
          >
            {sidebarOpen ? "Hide" : "Show"}
          </button>
        </div>
        <div
          className={`
            sticky top-4 bg-white shadow-md rounded-lg p-2
            ${sidebarOpen ? "block" : "hidden"}
            lg:block
          `}
        >
          <ul className="space-y-2 overflow-y-auto max-h-[calc(100vh-5rem)]">
            {BLOG_CATEGORIES.map((cat) => (
              <li key={cat.id}>
                <button
                  className={`w-full text-left px-3 py-2 rounded-md ${
                    selectedCategory === cat.id
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                  }`}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setSearchTerm("");
                  }}
                >
                  {cat.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm placeholder-gray-400"
            />
            <svg
              className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
              />
            </svg>
          </div>

          <div>
            <BlogEditorButton
              className="w-12 h-12 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition flex items-center justify-center shadow-md"
              title="Create Post"
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {isLoading && posts.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center text-gray-600">No posts found</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/post/${post.id}`}
                className="bg-white rounded-lg shadow-md hover:shadow-lg p-6 transition no-underline text-gray-900 hover:text-blue-600"
              >
                <h2 className="text-xl font-semibold mb-2 text-gray-900">
                  {post.title || "Untitled Post"}
                </h2>
                <div
                  className="text-gray-600 mb-4"
                  dangerouslySetInnerHTML={{
                    __html: post.content?.substring(0, 150) + "...",
                  }}
                />
                <div className="flex justify-between items-center text-xs text-gray-500 mt-4">
                  <div className="flex items-center gap-2">
                    <PenBox className="w-4 h-4 text-blue-500" />
                    <span className="font-medium">
                      {post.author || "Anonymous"}
                    </span>
                  </div>
                  <span className="bg-gray-100 px-2 py-1 rounded text-gray-600">
                    {post.createdAt.toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {hasMore && !isLoading && posts.length > 0 && (
          <div className="text-center m-5">
            <button
              onClick={fetchMorePosts}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Prepare;
